import {GitMergeIcon, GitPullRequestClosedIcon} from '@primer/octicons-react'
import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {useMergeabilityData, type MergeBoxPullRequest} from '../mergeability/use-mergeability-data'
import {Button} from '@primer/react'
import {useDeleteHeadRefMutation} from '@github-ui/mergebox/use-delete-head-ref-mutation'
import {FailureDialog} from '../../../FailureDialog'
import {BranchLink} from '../mergeability/shared/BranchLink'
import {announce} from '@github-ui/aria-live'
import {useRef} from 'react'
import {MergedDescription} from './MergedDescription'
import {RevertPRAction} from './RevertPRAction'

export function shouldShowDeleteHeadBranch(data: ReturnType<typeof useMergeabilityData>) {
  const {mergeBoxPullRequest, isClosedOrMerged} = data ?? {}

  return Boolean(mergeBoxPullRequest && isClosedOrMerged && mergeBoxPullRequest.viewerCanDeleteHeadRef)
}

export function DeleteHeadBranch({variant}: {variant?: 'default' | 'embedded'}) {
  const data = useMergeabilityData()

  if (!data) {
    return null
  }

  const {mergeBoxPullRequest, isClosedOrMerged} = data

  if (!isClosedOrMerged || !mergeBoxPullRequest.viewerCanDeleteHeadRef) {
    return null
  }

  if (mergeBoxPullRequest.state === 'MERGED') {
    return <DeleteMergedHeadBranch pullRequest={mergeBoxPullRequest} variant={variant} />
  }

  return <DeleteClosedHeadBranch pullRequest={mergeBoxPullRequest} variant={variant} />
}

function DeleteMergedHeadBranch({
  pullRequest,
  variant,
}: {
  pullRequest: MergeBoxPullRequest
  variant?: 'default' | 'embedded'
}) {
  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={GitMergeIcon} variant="done" emphasized />}
      title="Merged"
      emphasizedLeadingVisual
      itemName="delete-head-branch"
      variant={variant}
      description={
        <MergedDescription
          trailing={
            <>
              Delete the{' '}
              <BranchLink refName={pullRequest.headRefName} repository={pullRequest.headRepository} linkInTextBlock />{' '}
              branch to tidy up.
            </>
          }
        />
      }
      actionSlot={
        <span className="d-inline-flex flex-items-center gap-2">
          <DeleteBranchAction />
          <RevertPRAction />
        </span>
      }
    />
  )
}

function DeleteClosedHeadBranch({
  pullRequest,
  variant,
}: {
  pullRequest: MergeBoxPullRequest
  variant?: 'default' | 'embedded'
}) {
  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={GitPullRequestClosedIcon} variant="danger" emphasized />}
      title="Closed"
      emphasizedLeadingVisual
      itemName="delete-head-branch"
      description={
        <>
          This pull request is closed, but the{' '}
          <BranchLink refName={pullRequest.headRefName} repository={pullRequest.headRepository} linkInTextBlock />{' '}
          branch has unmerged commits.
        </>
      }
      variant={variant}
      actionSlot={<DeleteBranchAction />}
    />
  )
}

function DeleteBranchAction() {
  const deleteButtonRef = useRef<HTMLButtonElement>(null)
  const deleteHeadRefMutation = useDeleteHeadRefMutation({
    onSuccess: () => {
      // Move focus before query invalidation removes the action from the DOM.
      findPreviousTabbableElementOutsideRow(deleteButtonRef.current)?.focus()
      announce('Branch deleted')
    },
  })

  return (
    <>
      <Button
        ref={deleteButtonRef}
        loading={deleteHeadRefMutation.isPending}
        loadingAnnouncement="Deleting branch"
        onClick={() => deleteHeadRefMutation.mutate()}
        size="small"
      >
        Delete branch
      </Button>

      {deleteHeadRefMutation.isError && (
        <FailureDialog
          title="Failed to delete branch"
          message="An error occurred while deleting the head branch. Please try again."
          details={deleteHeadRefMutation.error.message}
          onRetry={() => deleteHeadRefMutation.mutate()}
          onDismiss={() => deleteHeadRefMutation.reset()}
        />
      )}
    </>
  )
}

function findPreviousTabbableElementOutsideRow(element: HTMLElement | null) {
  if (!element) return null

  const row = element.closest('li')
  const tabbableElements = Array.from(
    document.querySelectorAll<HTMLElement>('a[href], button, input, select, textarea, [tabindex]'),
  ).filter(candidate => candidate.tabIndex >= 0 && !candidate.matches(':disabled') && candidate.checkVisibility())
  const previousTabbableElements = tabbableElements.slice(0, tabbableElements.indexOf(element)).reverse()
  return previousTabbableElements.find(candidate => !row?.contains(candidate)) ?? null
}
